import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { ArrowLeft, Tag } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CardContent } from "@/components/ui/card";
import partnersLogo from "@/assets/partners_logo.png";
import type { PartnerType, SignUpData } from "@/pages/AuthPage";

interface CouponFormData {
  coupon: string;
}

interface CouponStepProps {
  signUpData: SignUpData;
  partnerType: PartnerType;
  onBack: () => void;
  onComplete: () => void;
}

export const CouponStep: React.FC<CouponStepProps> = ({ signUpData, partnerType, onBack, onComplete }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [couponExists, setCouponExists] = useState(false);
  const { signUp } = useAuth();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<CouponFormData>();

  const discount = partnerType === "influencer" ? 15 : 10;

  const onSubmit = async (data: CouponFormData) => {
    setIsLoading(true);
    setCouponExists(false);

    try {
      const couponCode = data.coupon.trim();

      // Create coupon in WooCommerce via public registration endpoint
      // This also double-checks uniqueness server-side
      const registerRes = await fetch("/api/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ coupon: couponCode, partnerType }),
      });

      if (!registerRes.ok) {
        const errData = await registerRes.json().catch(() => ({}));
        if (registerRes.status === 409) {
          setCouponExists(true);
          setIsLoading(false);
          return;
        }
        throw new Error(errData.error || "Erro ao criar cupom");
      }

      // Coupon created — now create the Supabase account
      const couponLowercase = couponCode.toLowerCase();
      await signUp(
        signUpData.email,
        signUpData.password,
        signUpData.name,
        signUpData.phone,
        signUpData.socialMedia,
        couponLowercase,
        partnerType
      );
      toast.success("Cadastro realizado com sucesso! Verifique seu e-mail para confirmar a conta.");
      onComplete();
    } catch (error: unknown) {
      console.error("Sign up error:", error);
      const errorMessage = error instanceof Error ? error.message : "Erro ao fazer cadastro. Tente novamente.";
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="auth-glass w-full rounded-2xl border border-white/10 bg-black/40 shadow-2xl backdrop-blur-xl">
      <div className="text-center pt-8 pb-4 px-4">
        <img src={partnersLogo} alt="Silva Partners" className="mx-auto h-16 sm:h-20 mb-3" />
        <h1 className="text-2xl sm:text-3xl text-foreground" style={{ fontFamily: "AwesomeSerif, serif" }}>Escolha o código do cupom:</h1>
      </div>
      <CardContent className="px-6 sm:px-8 pb-8">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div className="space-y-3">
            <div className="relative">
              <Tag className="absolute left-4 top-1/2 transform -translate-y-1/2 text-muted-foreground h-5 w-5" />
              <Input
                {...register("coupon", {
                  required: "Cupom é obrigatório",
                  minLength: {
                    value: 3,
                    message: "Cupom deve ter pelo menos 3 caracteres",
                  },
                  pattern: {
                    value: /^[a-zA-Z0-9]+$/,
                    message: "Use apenas letras e números",
                  },
                  onChange: () => setCouponExists(false),
                })}
                type="text"
                id="coupon"
                className="h-14 sm:h-16 text-2xl sm:text-3xl text-center font-bold tracking-widest uppercase pl-12 pr-12"
                placeholder="SEU CUPOM"
                autoFocus
              />
            </div>
            {errors.coupon && <p className="text-xs text-destructive text-center">{errors.coupon.message}</p>}
            {couponExists && (
              <p className="text-sm text-red-500 font-medium text-center">
                Este cupom já existe. Escolha outro código.
              </p>
            )}
          </div>

          <div className="space-y-2 text-center">
            <p className="text-xs sm:text-sm text-foreground leading-relaxed">
              Este cupom dá <span className="text-primary font-semibold">{discount}%</span> de desconto nos produtos <span className="text-primary font-semibold">Silva Nutrition</span>.
            </p>
            <p className="text-xs text-muted-foreground leading-relaxed">
              Este código é o que deve ser divulgado e usado no checkout para que suas vendas sejam elegíveis a receber comissão.
            </p>
          </div>

          <Button type="submit" disabled={isLoading} className="w-full h-11 sm:h-12 text-sm sm:text-base font-medium">
            {isLoading ? "Verificando..." : "Finalizar cadastro"}
          </Button>
        </form>

        <div className="mt-4 sm:mt-6 text-center">
          <Button
            variant="ghost"
            size="sm"
            onClick={onBack}
            className="text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Voltar
          </Button>
        </div>
      </CardContent>
    </div>
  );
};
